---
title: "Wenn KI Vertrauen kostet: Der soziale Preis der Automatisierung"
date: "2026-03-31"
excerpt: "Generative KI verändert Kommunikation – und das nicht immer zum Guten. Der Harvard Business Review zeigt, wie glatte KI-Texte das Vertrauen in Teams senken – und wo KI dennoch hilft."
metaTitle: "Wenn KI Vertrauen kostet: Der soziale Preis der Automatisierung"
metaDescription: "Neue HBR-Forschung zeigt: Zu glatte KI-Kommunikation untergräbt Vertrauen. Wie Teams psychologische Sicherheit wahren, wenn Maschinen mitschreiben."
language: "de"
slug: "wenn-ki-vertrauen-kostet"
author: "Kevin Rassner"
authorUrl: "https://rassner-dev.org"
canonicalUrl: "https://rassner-dev.org/blog/wenn-ki-vertrauen-kostet"
publishedOn: "rassner-dev.org"
tags: ["KI","Vertrauen","Teamdynamik"]

---

## Wenn KI Vertrauen kostet: Der soziale Preis der Automatisierung

Der Harvard Business Review warnt in seinem März-2026-Report „How AI Damages Work Relationships – and Where It Can Actually Help“ ([HBR, 2026](https://hbr.org)) vor einem unterschätzten Nebeneffekt generativer KI: **Verlust an Vertrauen**. 

### Workslop – zu glatt, zu seelenlos

Je stärker Mitarbeitende den Eindruck haben, dass Nachrichten, Feedback oder Ideen KI-generiert sind, desto geringer ihre Wertschätzung der Absender:innen. Die Wahrnehmung: *künstliche Empathie statt echter Aufmerksamkeit*. 
Das Phänomen trägt mittlerweile einen Namen: *Workslop*. 

### Warum Glätte gefährlich ist

Linda Hills Konzept der *Creative Abrasion* (Harvard Business Press) beschreibt, dass Reibung zwischen unterschiedlichen Perspektiven der Treibstoff kreativer Kooperation ist. Wenn KI-Kommunikation alle Kanten glättet, verschwindet auch diese produktive Reibung – und damit Lernen.

### Wo KI hilfreich bleibt

Die HBR-Autoren differenzieren klar: 
- Bei **analytischen, strukturierten Aufgaben** (z. B. Reportgenerierung, Tabellen, Policy-Summarys) verbessert KI Qualität und Geschwindigkeit. 
- In **emotionalen oder sozialen Prozessen** (Feedback, Konflikt, Anerkennung) führt Automation dagegen zu Distanz. 

### Kulturabhängigkeit: Psychologische Sicherheit als Puffer

In Organisationen mit hoher psychologischer Sicherheit (Amy Edmondson, *The Fearless Organization*, Wiley, 2019) sind KI-Tools besser integriert. Teams können dort KI nutzen, **ohne ihre Authentizität zu verlieren**, weil Kommunikation transparenter bleibt. 

Selbstorganisierte Teams, die Autonomie leben, nutzen KI als Werkzeug – nicht als Stimme. Das zeigen Beispiele aus Semco Style Companies oder Spotify Squads: KI generiert Struktur, Menschen geben Sinn. 

### Praxisempfehlungen

- **Transparenz ist Vertrauenswährung.** Offenlegen, wann KI unterstützt – nicht verstecken. 
- **Fokus auf Hybrid-Arbeitsteilung:** KI für Analyse, Mensch für Resonanz. 
- **Führung als Sicherungsraum:** Emotionen nicht outsourcen.
